import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { accountingApi, formatInr } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";
import { toast } from "sonner";

type Tab = "GST" | "TDS";

export default function AccountingTaxPage() {
  const { hospitalId, canPost } = useAccountingHospitalContext();
  const [tab, setTab] = useState<Tab>("GST");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const gstQ = useQuery({
    queryKey: ["accounting-gst", hospitalId, fromDate, toDate],
    queryFn: () =>
      accountingApi.gstSummary(hospitalId, {
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      }),
    enabled: !!hospitalId && tab === "GST",
  });

  const tdsQ = useQuery({
    queryKey: ["accounting-tds", hospitalId, fromDate, toDate],
    queryFn: () =>
      accountingApi.tdsSummary(hospitalId, {
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      }),
    enabled: !!hospitalId && tab === "TDS",
  });

  const seedTax = async () => {
    try {
      const r = (await accountingApi.seedTaxRules(hospitalId)) as {
        gstCreated?: number;
        tdsCreated?: number;
      };
      toast.success(
        `Tax rules seeded (GST +${r?.gstCreated ?? 0}, TDS +${r?.tdsCreated ?? 0})`
      );
      gstQ.refetch();
      tdsQ.refetch();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Seed failed");
    }
  };

  const exportCsv = async () => {
    if (!hospitalId) return;
    try {
      await accountingApi.downloadTaxCsv(tab === "GST" ? "gst" : "tds", hospitalId, {
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
    } catch {
      toast.error("CSV export failed");
    }
  };

  const loading =
    tab === "GST" ? gstQ.isLoading || gstQ.isFetching : tdsQ.isLoading || tdsQ.isFetching;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium">GST / TDS</h2>
        <p className="mt-1 text-sm text-slate-500">
          Configurable tax engines and books summaries for return prep (not e-filing).
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {(["GST", "TDS"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              tab === t ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40 space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="w-40 space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <Button type="button" variant="outline" onClick={exportCsv}>
          Export CSV
        </Button>
        {canPost ? (
          <Button type="button" variant="secondary" onClick={seedTax}>
            Seed system tax rules
          </Button>
        ) : null}
      </div>

      {tab === "GST" && gstQ.data?.note ? (
        <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {gstQ.data.note}
          {gstQ.data.gstin ? ` GSTIN: ${gstQ.data.gstin}.` : null}
        </p>
      ) : null}
      {tab === "TDS" && tdsQ.data?.note ? (
        <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {tdsQ.data.note}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : tab === "GST" ? (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Output GST</div>
              <div className="text-lg font-medium tabular-nums">
                {formatInr(gstQ.data?.summary?.outputGst ?? 0)}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Input GST</div>
              <div className="text-lg font-medium tabular-nums">
                {formatInr(gstQ.data?.summary?.inputGst ?? 0)}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Net payable (prep)</div>
              <div className="text-lg font-medium tabular-nums">
                {formatInr(gstQ.data?.summary?.netPayable ?? 0)}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Voucher</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">CGST</TableHead>
                  <TableHead className="text-right">SGST</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(gstQ.data?.rows ?? []).map((r, i) => (
                  <TableRow key={`${r.voucherNumber}-${i}`}>
                    <TableCell>{r.voucherDate}</TableCell>
                    <TableCell>{r.voucherNumber}</TableCell>
                    <TableCell className="text-slate-500">{r.reference || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.gstRate}%</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(r.gstAmount)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(r.cgst)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(r.sgst)}</TableCell>
                  </TableRow>
                ))}
                {!gstQ.data?.rows?.length ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-sm text-slate-500">
                      No posted output GST lines in range.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Withheld</div>
              <div className="text-lg font-medium tabular-nums">
                {formatInr(tdsQ.data?.summary?.withheld ?? 0)}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Remitted</div>
              <div className="text-lg font-medium tabular-nums">
                {formatInr(tdsQ.data?.summary?.remitted ?? 0)}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Payable</div>
              <div className="text-lg font-medium tabular-nums">
                {formatInr(tdsQ.data?.summary?.payable ?? 0)}
              </div>
            </div>
          </div>
          {(tdsQ.data?.activeRules?.length ?? 0) > 0 ? (
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nature</TableHead>
                    <TableHead>Old §</TableHead>
                    <TableHead>Current §</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Txn threshold</TableHead>
                    <TableHead>Scope</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tdsQ.data!.activeRules.map((r) => (
                    <TableRow key={r.ruleCode}>
                      <TableCell>{r.natureOfPayment}</TableCell>
                      <TableCell>{r.oldSectionReference || "—"}</TableCell>
                      <TableCell>{r.currentSectionReference || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.ratePercent}%</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatInr(r.thresholdPerTxn)}
                      </TableCell>
                      <TableCell className="text-slate-500">{r.scopeKey}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Voucher</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead className="text-right">Withheld</TableHead>
                  <TableHead className="text-right">Remitted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(tdsQ.data?.rows ?? []).map((r, i) => (
                  <TableRow key={`${r.voucherNumber}-${i}`}>
                    <TableCell>{r.voucherDate}</TableCell>
                    <TableCell>{r.voucherNumber}</TableCell>
                    <TableCell>{r.eventType || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.withheld ? formatInr(r.withheld) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.remitted ? formatInr(r.remitted) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
                {!tdsQ.data?.rows?.length ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-sm text-slate-500">
                      No posted TDS lines in range.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
